import { ref } from 'vue'
import { signOut } from 'firebase/auth'
import { handleHttpError, triggerNetworkError, triggerAccessDenied } from '@/composables/useGlobalError'

function isNetworkError(error) {
  return error instanceof TypeError && error.message.includes('fetch')
}

export class Api {
  constructor(options = {}) {
    this._options = { api_url: null, ...options }
    this._initializeErrors = ref([])
    this._auth = ref(null)
    this._forbidden = ref(false)
    this._userId = ref(null)
    this._vueApp = null

    this._validateOptions(options)
  }

  _validateOptions(options) {
    const requiredParameters = ['api_url']

    for (const param of requiredParameters) {
      if (!options[param]) {
        const error = `No specified required parameter: ${param}`
        console.warn(`[API] ${error}`)
        this._initializeErrors.value.push(error)
      }
    }
  }

  install(app) {
    if (app._context.provides.api || app.config.globalProperties.api) {
      if (this._options.debug) {
        console.log('[API] App already provides property with key "api". Need to make changes to the code')
      }
      return
    }

    app.provide('api', this)
    app.config.globalProperties.$api = this
    this._vueApp = app
  }

  async _request(path = '', options = {}) {
    const currentUser = this._auth?.currentUser
    if (!currentUser) {
      throw new Error('No authenticated user')
    }

    try {
      const idToken = await currentUser.getIdToken()

      const response = await fetch(this._options.api_url + path, {
        ...options,
        headers: {
          ...options.headers,
          Authorization: idToken,
        },
      })

      return await this._handleResponse(response, options)
    } catch (error) {
      this._handleError(error)
      throw error
    }
  }

  async _handleResponse(response, options) {
    const { status } = response

    if (status === 401) {
      await signOut(this._auth)
      return response.json()
    }

    if (status === 403) {
      this._forbidden.value = true
      triggerAccessDenied()
      const data = await response.json()
      return { ...data, status, ok: false }
    }

    if (status >= 400) {
      const data = await response.json()
      handleHttpError(status, data, options)
      return { ...data, status, ok: false }
    }

    if (status === 200) {
      this._userId.value = response.headers.get('x-uid')
    }

    if (options.rawResponse) {
      return response
    }

    return response.json()
  }

  _handleError(error) {
    if (isNetworkError(error)) {
      triggerNetworkError()
    }
  }

  get initializeErrors() {
    return this._initializeErrors.value
  }

  get request() {
    return this._request.bind(this)
  }

  get auth() {
    return this._auth.value
  }

  set auth(v) {
    this._auth = v
  }

  get forbidden() {
    return this._forbidden.value
  }

  set forbidden(v) {
    this._forbidden.value = v
  }

  get userId() {
    return this._userId.value
  }
}